import React, { createContext, useContext, useEffect, useState } from 'react';

import { useAppSelector } from 'hooks';

interface IEpicProviderParams {
  children: any;
}

// Local to encapsulate global state
class EpicSingleton {
  private static instance: EpicSingleton | null = null;
  private listener: Map<string, Map<string, IEpicListener<any>>>;

  constructor() {
    this.listener = new Map();
  }

  static getInstance() {
    if (!EpicSingleton.instance) {
      EpicSingleton.instance = new EpicSingleton();
    }
    return EpicSingleton.instance;
  }

  subscribe = ({ eventId, id, callback }: IEpicEventListener) => {
    if (!this.listener.get(eventId)) {
      this.listener.set(eventId, new Map());
    }
    this.listener.get(eventId)?.set(id, callback);
  };

  unsubscribe = ({ eventId, id }: IEpicUnsubscribe) => {
    this.listener.get(eventId)?.delete(id);
  };

  messageProcessor = (token: string | undefined) => async (messageEvent: MessageEvent) => {
    const { error, errorCodes, EventName, EventArgs } = messageEvent.data;
    if (error) {
      // eslint-disable-next-line no-console
      console.error(`An error was return by epic: ${error}`);
    }
    if (errorCodes && errorCodes.length) {
      // eslint-disable-next-line no-console
      console.error(`An error code was return by epic: ${errorCodes}`);
    }
    if (EventName) {
      const listeners = this.listener.get(EventName) || new Map();
      for (const listener of listeners.values()) {
        await listener(EventArgs);
      }
      if (EventName === 'Epic.Common.RequestToCloseApp') {
        publish({
          action: 'Epic.Common.CloseAppResponse',
          args: {
            CanClose: true
          },
          token
        });
      }
    }
  };
}

const publish = (message: IEpicOutboundMessage) => {
  window.parent.postMessage(message, '*');
};

const EpicContext = createContext<IEpicContext>({
  subscribe: (sub) => EpicSingleton.getInstance().subscribe(sub),
  unsubscribe: (sub) => EpicSingleton.getInstance().unsubscribe(sub),
  publish: () => {},
  isReady: false
});

const eventBus: { EventName: string; EventArgs: any }[] = [
  {
    EventName: 'Epic.Common.RequestToCloseApp',
    EventArgs: {
      PauseDuration: '1000'
    }
  }
];

export const EpicProvider = ({ children }: IEpicProviderParams) => {
  const isEmbedded = useAppSelector((state) => state.settings.embedded);
  const [initialized, setInitialized] = useState(false);
  const [token, setToken] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (isEmbedded && !initialized) {
      setInitialized(true);
      window.addEventListener('message', (messageEvent: MessageEvent) => {
        if (messageEvent.data['token']) setToken(messageEvent.data['token']);
      });
      publish({
        action: 'Epic.Clinical.Informatics.Web.InitiateHandshake',
        args: { SubscriptionRequests: eventBus }
      });
    }
  }, [isEmbedded, initialized]);

  useEffect(() => {
    if (isEmbedded && initialized && window !== window.parent) {
      // Refresh the listener to keep the token up to date if it changes
      const messager = EpicSingleton.getInstance().messageProcessor(token);
      window.addEventListener('message', messager);
      return () => window.removeEventListener('message', messager);
    }
  }, [initialized, isEmbedded, token]);

  const ctx: IEpicContext = {
    subscribe: (sub) => EpicSingleton.getInstance().subscribe(sub),
    unsubscribe: (sub) => EpicSingleton.getInstance().unsubscribe(sub),
    publish: (message: IEpicPublish) => publish({ ...message, token }),
    isReady: !!token
  };

  return <EpicContext.Provider value={ctx}>{children}</EpicContext.Provider>;
};

export const useEpic = () => useContext(EpicContext);
